// @flow
import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { CSVLink } from 'react-csv';
import debounce from 'lodash.debounce';
import {
  Card,
  Table,
  Tooltip,
  Badge,
  Row,
  Col,
} from 'antd';
import {
  SpButton,
  FilterWrapper,
  SpText,
} from 'components/DesignKit';
import queryString from 'query-string';
import moment from 'moment-timezone';
import { useLocation } from 'react-router-dom';
import Filters from 'components/Filters';
import styled from 'styled-components';

import Loading from 'components/Loading';

import getBackground from 'utils/getServiceBackground';
import getServiceStatus from 'utils/getServiceStatus';
import getFilterOperators from 'utils/getFilterOperators';

import {
  servicesActions,
  filterParamsActions,
} from 'store/actions';
import { FILTERS_SPECS } from 'store/actionTypes';

import RBAC from 'components/rbac';
import FilterIcon from 'components/NavIcons/filterIcon';
import ExportIcon from 'components/NavIcons/exportIcon';
import {
  ROLES,
  PAGES,
  FILTERS_TYPES,
  FILTER_STATUSES,
} from 'appconstants';

const SERVICES_FILTERS_TYPES = FILTERS_TYPES.SERVICES;
const ServicesTypes = FILTER_STATUSES.SERVICES;

// $FlowFixMe
const StatusTag = styled('div')({
  borderRadius: '10px',
  display: 'inline-block',
  paddingLeft: '5px',
  paddingRight: '5px',
  marginLeft: 'auto',
  marginRight: 'auto',
  color: '#434343',
  fontWeight: 600,
  minWidth: '75px',
  textAlign: 'center',
});

type Props = {
  fetchServices: Function,
  services: Array<Object>,
  loading: boolean,
  test: boolean,
  totalElements: number,
  selectedAccount: Object,
  history: {
    push: Function,
  },
  setFilterData: Function,
  preserveFilters: Object,
};

const ServiceList = (props: Props) => {
  const {
    loading,
    fetchServices,
    services,
    totalElements,
    test,
    selectedAccount,
    history,
    setFilterData,
    preserveFilters,
  } = props;

  const location = useLocation();
  const [pagination, setPagination] = useState({});
  const myRef = useRef();
  const { pathname } = location;
  const [open, setOpen] = useState(false);
  const [filterTypes, setFilterTypes] = useState(SERVICES_FILTERS_TYPES);
  const [fiterOperators, setFilterOperators] = useState([]);
  const [fileterServices, setServices] = useState((preserveFilters && preserveFilters.fileterServices)
    ? (preserveFilters && preserveFilters.fileterServices)
    : [{
      filterName: '',
      filterOperator: '',
      filterValue: '',
      filterNameDisplay: '',
      checked: true,
    }]);
  const [count, setCount] = useState(null);
  const [fillColor, setFillColor] = useState(false);
  const [filterColor, setFilterColor] = useState(false);
  const preserveParam = ((preserveFilters && preserveFilters.pathname) === pathname)
    ? queryString.parse(preserveFilters.search)
    : setFilterData({
      pathname,
      search: '',
      fileterServices: [{
        filterName: '',
        filterOperator: '',
        filterValue: '',
        filterNameDisplay: '',
        checked: true,
      }],
    });

  const exportData = services ? services.map((values) => ({
    serviceNo: values.serviceNo,
    serviceRequest: values.serviceRequest,
    terminalSerialNo: values.terminalSerialNo || '',
    terminalTid: values.terminalTid || '',
    status: getServiceStatus(values.status),
    updatedAt: moment.utc(values.updatedAt)
      .tz(selectedAccount.timezone)
      .format('MMM DD, YYYY, hh:mm a'),
  })) : '';

  const exportHeaders = [
    { label: 'REQUEST ID', key: 'caseNumber' },
    { label: 'TYPE', key: 'serviceRequest' },
    { label: 'DEVICE NAME', key: 'terminalSerialNo' },
    { label: 'TID', key: 'terminalTid' },
    { label: 'STATUS', key: 'status' },
    { label: 'LAST UPDATED', key: 'updatedAt' },
  ];

  const debouncedFetchServices = debounce(fetchServices, 2000, { leading: true });

  const getData = (query) => {
    const parsed = queryString.parse(query);
    const currentPage = parsed.pageNum || 1;
    const sortBy = parsed.sortBy || 'createdAt';
    const direction = parsed.direction || 'DESC';
    const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
    const filterParam = parsed.filterParams || '';
    const filterParams = (preserveParam ? preserveParam.filterParams : filterParam || '');
    debouncedFetchServices({ currentPage, sortParams, filterParams });
  };

  useEffect(() => {
    getData(location.search);
  }, [test, selectedAccount, location]);

  const handleClick = e => {
    if (myRef.current && myRef.current.contains(e.target)) {
      return;
    }
    if (e.target.value === undefined) {
      setOpen(false);
    }
  };

  useEffect(() => {
    const parsed = queryString.parse(location.search);
    const currentPage = parsed.pageNum || 1;
    setPagination(existingElements => ({
      ...existingElements,
      total: totalElements,
      current: parseInt(currentPage, 10),
      showTotal: total => `Total ${total} items`,
    }));
  }, [services]);

  const selectedRecord = (selectedRequest: Object) => {
    const { gid } = selectedRequest;
    history.push({
      pathname: `${PAGES.SERVICES}/${gid}`,
      state: location.search,
    });
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  const handleTableChange = (page, sorter, filters) => {
    const parsed = queryString.parse(location.search);
    const filterParams = parsed.filterParams || '';
    const tempPage = page.current;
    let tempParams = '';
    if (filters.order === 'ascend') {
      tempParams = `&sortBy=${filters.field}&direction=ASC`;
    } else if (filters.order === 'descend') {
      tempParams = `&sortBy=${filters.field}&direction=DESC`;
    } else {
      tempParams = '';
    }
    if (location.search && filterParams) {
      const filterParam = encodeURIComponent(filterParams);
      history.push({
        pathname: PAGES.SERVICES,
        search: `?filterParams=${filterParam}&pageNum=${tempPage}${tempParams}`,
      });
    } else {
      history.push({
        pathname: PAGES.SERVICES,
        search: `?pageNum=${tempPage}${tempParams}`,
      });
    }
  };

  const onCheck = (e, index) => {
    const list = [...fileterServices];
    if (fileterServices.length !== 1) {
      if (!e) {
        list.splice(index, 1);
        const filterTypeOptions = SERVICES_FILTERS_TYPES.filter(({ value: id1 }) => !list.some(({ filterName: id2 }) => id2 === id1));
        setFilterTypes(filterTypeOptions);
      } else {
        list[index].checked = e;
      }
    }
    setServices(list);
  };

  const handleChange = (e, index) => {
    const list = [...fileterServices];
    if (e[1] === 'serviceType') {
      list[index].filterType = 'Dropdown';
    } else {
      list[index].filterType = 'String';
    }
    list[index].filterName = e[1];
    list[index].filterNameDisplay = e[0];
    list[index].filterOperator = '';
    list[index].filterValue = '';
    const filterTypeOptions = SERVICES_FILTERS_TYPES.filter(({ value: id1 }) => !list.some(({ filterName: id2 }) => id2 === id1));
    setServices(list);
    setFilterTypes(filterTypeOptions);
    setFilterOperators(getFilterOperators(e[1]));
  };

  const handleValue = (e, index) => {
    let selectedValue = '';
    if (!e.currentTarget) {
      selectedValue = e;
    }
    if (e.currentTarget && e.currentTarget.value) {
      selectedValue = e.currentTarget.value;
    }
    const list = [...fileterServices];
    list[index].filterValue = selectedValue;
    setServices(list);
  };

  const handleOperator = (e, index) => {
    const list = [...fileterServices];
    list[index].filterOperator = e;
    if (list[index].filterValue) {
      handleValue(list[index].filterValue, index);
    }
    setServices(list);
  };

  const handleAddClick = () => {
    const list = [...fileterServices];
    setServices([...list, {
      filterName: '',
      filterOperator: '',
      filterValue: '',
      filterNameDisplay: '',
      checked: true,
    }]);
  };

  const resetFilters = () => {
    setFilterData({
      pathname,
      search: '',
    });
    setServices([{
      filterName: '',
      filterOperator: '',
      filterValue: '',
      filterNameDisplay: '',
      checked: true,
    }]);
    setFilterTypes(SERVICES_FILTERS_TYPES);
    setCount(null);
    history.push(PAGES.SERVICES);
  };

  const applyFilters = () => {
    const locationsFilters = fileterServices.filter((item) => item.checked);
    for (let i = 0; i < locationsFilters.length; i += 1) {
      if (!locationsFilters[i].filterName || !locationsFilters[i].filterOperator || !locationsFilters[i].filterValue) {
        return;
      }
    }
    if (locationsFilters[0].filterValue) {
      setCount(locationsFilters && locationsFilters.length);
      for (let i = 0; i < locationsFilters.length; i += 1) {
        if (locationsFilters[i].checked) {
          if (locationsFilters[i].filterName === 'createdAt') {
            locationsFilters[i] = locationsFilters[i].filterValue;
          } else {
            locationsFilters[i] = `${locationsFilters[i].filterName}.${locationsFilters[i].filterOperator}=${locationsFilters[i].filterValue}`;
          }
        }
      }

      const filterParams = encodeURIComponent(locationsFilters.join('&'));
      const parsed = queryString.parse(location.search);
      const currentPage = 1;
      const sortBy = parsed.sortBy || 'createdAt';
      const direction = parsed.direction || 'DESC';
      const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
      if (location.search && parsed.sortBy && parsed.pageNum && parsed.direction) {
        setFilterData({
          pathname,
          search: `?filterParams=${filterParams}&pageNum=${currentPage}${sortParams}`,
          fileterServices,
        });
        history.push({
          pathname: PAGES.SERVICES,
          search: `?filterParams=${filterParams}&pageNum=${currentPage}${sortParams}`,
        });
      } else {
        setFilterData({
          pathname,
          search: `?filterParams=${filterParams}`,
          fileterServices,
        });
        history.push({
          pathname: PAGES.SERVICES,
          search: `?filterParams=${filterParams}`,
        });
      }
      setOpen(false);
    }
  };

  const columns = [
    {
      title: 'REQUEST ID',
      dataIndex: 'caseNumber',
      width: '15%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (caseNumber) => (
        <Tooltip placement="top" title="Click here to view details">
          <span style={{ cursor: 'pointer' }}>
            {caseNumber || <>&#8211;</>}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'TYPE',
      dataIndex: 'serviceRequest',
      width: '18%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (serviceRequest) => (
        <Tooltip placement="top" title="Click here to view details">
          <span style={{ cursor: 'pointer' }}>
            {serviceRequest || <>&#8211;</>}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'DEVICE NAME',
      dataIndex: 'terminalSerialNo',
      width: '18%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (terminalSerialNo, record) => (
        <Tooltip placement="top" title="Click here to view details">
          <span style={{ cursor: 'pointer' }}>
            {record.terminal ? record.terminal.deviceName : <>&#8211;</>}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'TID',
      dataIndex: 'terminalTid',
      width: '15%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (terminalTid, record) => (
        <Tooltip placement="top" title="Click here to view details">
          <span style={{ cursor: 'pointer' }}>
            {record.terminal ? record.terminal.terminalTid : <>&#8211;</>}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      width: '15%',
      align: 'left',
      sorter: false,
      render: (status) => (
        <Tooltip placement="top" title="Click here to view details">
          <span style={{ cursor: 'pointer' }}>
            <StatusTag style={{ backgroundColor: getBackground(status) }}>{getServiceStatus(status)}</StatusTag>
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'LAST UPDATED',
      dataIndex: 'updatedAt',
      width: '25%',
      align: 'left',
      render: (updatedAt) => {
        if (updatedAt && selectedAccount && selectedAccount.timezone) {
          const tzDate = moment
            .utc(updatedAt)
            .tz(selectedAccount.timezone)
            .format('MMM DD, YYYY, hh:mm a');
          return (<span>{tzDate}</span>);
        }
        return (
          <>&#8211;</>
        );
      },
      sorter: true,
      defaultSortOrder: 'descend',
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
  ];

  if (loading) {
    return <Loading />;
  }

  return (
    <Card>
      <Row className="my-2">
        <div className="d-flex justify-content-end">
          <Col>
            <RBAC
              permit={
                [ROLES.OWNER, ROLES.ADMINISTRATOR, ROLES.DEVELOPER, ROLES.OPERATIONS, ROLES.ANALYST,
                  ROLES.SUPPORT, ROLES.SUPERUSER, ROLES.OFFICEMANAGER, ROLES.OFFICESUPPORT]
              }
            >
              <SpButton
                type="secondary"
                shape="round"
                className="mr-3"
                ghost
                onClick={() => history.push(PAGES.SUPPORT)}
              >
                {'\u002B'}&nbsp;New Service Request
              </SpButton>
            </RBAC>
            <span>
              {
                exportData !== null
                  ? (
                    <Tooltip placement="left" title="Click here to export Service request">
                      <CSVLink
                        target="_blank"
                        className="mr-3"
                        filename="Services Reports.csv"
                        data={exportData}
                        headers={exportHeaders}
                        onMouseEnter={() => setFillColor(true)}
                        onMouseLeave={() => setFillColor(false)}
                      >
                        <ExportIcon
                          fill={fillColor
                            ? '#279dfe'
                            : '#C0C0C0'}
                        />
                      </CSVLink>
                    </Tooltip>
                  ) : null
              }
            </span>
            <span ref={myRef}>
              <span
                onClick={() => setOpen(!open)}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setFilterColor(true)}
                onMouseLeave={() => setFilterColor(false)}
              >
                {open && (
                  <Badge count={count} style={{ backgroundColor: '#0090ff' }}>
                    <FilterIcon
                      fill={filterColor
                        ? '#279dfe'
                        : '#C0C0C0'}
                    />
                  </Badge>
                )}
                {!open && (
                  <Tooltip placement="left" title="Click here to filter Service request">
                    <Badge count={count} style={{ backgroundColor: '#0090ff' }}>
                      <FilterIcon
                        fill={filterColor
                          ? '#279dfe'
                          : '#C0C0C0'}
                      />
                    </Badge>
                  </Tooltip>
                )}
              </span>
              {open && (
                <FilterWrapper
                  style={{ marginLeft: '-60px' }}
                >
                  <Filters
                    filtersGrid={fileterServices}
                    filterTypes={filterTypes}
                    handleChange={handleChange}
                    onCheck={onCheck}
                    fiterOperators={fiterOperators}
                    handleOperator={handleOperator}
                    handleValue={handleValue}
                    stuses={ServicesTypes}
                  />
                  <div className="px-4 mt-2">
                    {
                      fileterServices.length < SERVICES_FILTERS_TYPES.length && (
                        <div className="mb-2 ml-3" style={{ cursor: 'pointer' }}>
                          <SpText
                            fontWeight="600"
                            color="#279dfe"
                            onClick={handleAddClick}
                          >
                            +&nbsp; Add another filter
                          </SpText>
                        </div>
                      )
                    }
                  </div>
                  <div className="d-flex mb-2 justify-content-end mr-3">
                    <SpButton
                      type="secondary"
                      shape="round"
                      className="mr-2"
                      ghost
                      onClick={resetFilters}
                    >
                      Clear
                    </SpButton>
                    <SpButton
                      type="primary"
                      shape="round"
                      onClick={applyFilters}
                    >
                      Save
                    </SpButton>
                  </div>
                </FilterWrapper>
              )}
            </span>
          </Col>
        </div>
      </Row>

      <RBAC
        fallback={(
          <Table
            columns={columns}
            rowKey={(record) => record.gid}
            dataSource={services}
            pagination={pagination}
            onChange={handleTableChange}
          />
        )}
        permit={[ROLES.OWNER, ROLES.ADMINISTRATOR, ROLES.DEVELOPER, ROLES.OPERATIONS, ROLES.SUPERUSER, ROLES.OFFICEMANAGER, ROLES.OFFICESUPPORT]}
      >
        <Table
          columns={[...columns]}
          rowKey={(record) => record.gid}
          onRow={(record) => ({
            onClick: () => {
              selectedRecord(record);
            },
          })}
          rowClassName={() => 'ant-table-clickable-row'}
          dataSource={services}
          pagination={pagination}
          onChange={handleTableChange}
        />
      </RBAC>
    </Card>
  );
};

const mapStateToProps = (state) => ({
  services: state.services.content,
  locations: state.locations.content,
  loading: state.loading.loading,
  submitting: state.loading.submitting,
  test: state.account.test,
  totalElements: state.services.totalElements,
  isSuperUser: state.user.superUser,
  selectedAccount: state.account.selectedAccount,
  preserveFilters: state.filterParams.preserveFilters,
});

const mapDispatchToProps = (dispatch) => ({
  fetchServices: param => dispatch(servicesActions.fetchServices({
    payload: param,
  })),
  setFilterData: (value) => dispatch(filterParamsActions.setFilterData({
    type: FILTERS_SPECS.SET_FILTER_DATA,
    payload: value,
  })),
});

// $FlowFixMe
export default connect(mapStateToProps, mapDispatchToProps)(ServiceList);
